import { setup, assign, enqueueActions, fromPromise } from "xstate";
import { ResponseEnvelope } from "areas/common/models/responseEnvelope";
import constants from "constants.json";
import { redirectToSignIn } from "areas/account/utilities/signinRedirector";
import { openLoadingScreen } from "areas/layout/utilities/closeLoadingScreen";
import { datadogLogs } from '@datadog/browser-logs';
import { PaygError } from "areas/common/models/paygError";
import { getCorrelationId } from 'infrastructure/network/correlationId';

interface Context {
    url?: string;
    method: "get" | "post" | "put" | "delete";
    requestData?: object | FormData;
    responseEnvelope?: ResponseEnvelope;
    isChildMachine: boolean;
    callCount: number;
    abortController: AbortController;
    retryCount: number;
    contentType: string;
    bearerToken?: string;
    unauthorisedAutoRedirect: boolean; // if response is 401, automatically redirect to sign in screen.
    triggerSiteMessageChangeMessage: boolean;
}

export interface HttpMachineResponse {
    responseEnvelope: ResponseEnvelope | undefined,
}
export enum States {
    Init = "Init",
    Requesting = "Requesting",
    Success = "Success",
    ConnectionError = "ConnectionError",
    RequestError = "RequestError",
    PendingRetry = "PendingRetry",
    Aborted = "Aborted",
    Unauthorised = "Unauthorised",
}

export enum Events {
    Abort = "Abort",
    Refresh = "Refresh",
    Execute = "Execute",
}

enum Guards {
    IsRequestError = "IsRequestError",
}

enum Actions {
    AssignData = "AssignData",
    AssignErrorMessage = "AssignErrorMessage",
}

type ExecuteEvent = { type: Events.Execute, requestData: object | FormData }
type EventTypes = { type: Events.Abort } | { type: Events.Refresh } | ExecuteEvent;

export type InputType = Required<Pick<Context, "url" | "method">> & Partial<Pick<Context, "requestData" | "isChildMachine" | "retryCount" | "contentType" | "unauthorisedAutoRedirect" | "triggerSiteMessageChangeMessage">>;

export const HttpMachine = setup({
        types: {} as {
            context: Context,
            event: EventTypes,
            input: InputType,
            output: HttpMachineResponse,
        },
        actions: {
            [Actions.AssignErrorMessage]: assign(({ event }) => {
                //console.debug(`HttpMachine: AssignErrorMessage: event = `, event);
                const result = {
                    responseEnvelope: event?.error?.responseEnvelope 
                        ?? {
                            data: null,
                            totalCount: null,
                            problemDetails: {
                                detail: event.output,
                                status: event.error.status,
                                title: "Something went wrong",
                                validationErrors: null,
                            },
                        }
                }
                //console.debug(`HttpMachine: AssignErrorMessage: result = `, result);
                return result;
            }),
        },
        guards: {
            [Guards.IsRequestError]: ({ event }) => {
                //console.debug(`HttpMachine: Guards.IsRequestError: event = `, event);
                const isRequestError =
                    event.error.status >= 400 &&
                    event.error.status < 500 &&
                    event.error.status !== 429; /* Too Many Requests - used for Throttling */
                //console.debug(`HttpMachine: Guards.IsRequestError: isRequestError = `, isRequestError);
                return isRequestError;
            }
        },
        actors: {
            fetch: fromPromise(async ({ input }: { input: { context: Context } }) => {
                const { context } = input;
                const response = await doFetch(
                    context.url ?? "no url supplied",
                    context.method,
                    context.requestData,
                    context.contentType,
                    context.bearerToken,
                    context.abortController.signal
                );
                return response;
            }),
        },
        delays: {
            pendingRetryTimeout: ({ context }) => {
                console.info(
                    `PendingRetry: delaying for ${
                        context.callCount * 100
                    } milliseconds before transitioning to Fetching...`
                );
                return context.callCount * 100;
            },
        },
    })
    .createMachine({
        id: "httpMachine",
        initial: States.Init,
        context:  ({ input }) => ({
            url: input.url,
            method: input.method,
            requestData: input.requestData,
            responseEnvelope: undefined,
            callCount: 0,
            abortController: new AbortController(),
            isChildMachine: input.isChildMachine ?? false,
            retryCount: input.retryCount ?? constants.Http.RetryCount,
            contentType: input.contentType ?? "application/json",
            unauthorisedAutoRedirect: input.unauthorisedAutoRedirect ?? true,
            triggerSiteMessageChangeMessage: input.triggerSiteMessageChangeMessage ?? true,
        }),
        states: {
            [States.Init]: {
                always: [
                    {
                        guard: ({ context, event }) => {
                            return !context.url || !context.method;
                        },
                        target: States.Aborted,
                        actions: ({ context, event }) => {
                            console.error(`HttpMachine: url or method not provided to machine.`);
                        },
                    },
                    {
                        guard: ({ context, event }) => {
                            return context.isChildMachine === true || context.method === "get";
                        },
                        target: States.Requesting,
                    },
                ],
                on: {
                    [Events.Execute]: {
                        target: States.Requesting,
                        actions: assign({
                            requestData: ({ context, event }) => (event as ExecuteEvent).requestData
                        })
                    }
                }
            },
            [States.Requesting]: {
                invoke: {
                    id: "request",
                    src: "fetch",
                    input: ({ context }) => ({ context }),
                    onDone: {
                        target: States.Success,
                        actions: [
                            assign({
                                responseEnvelope: ({ event }) => {
                                    //console.debug(`HttpMachine: Requesting: onDone: AssignData: event = `, event);
                                    return event.output.responseEnvelope;
                                },
                            }),
                            ({ context, event }) => {
                                console.debug(`HttpMachine: Requesting: onDone: event = `, event);
                                console.debug(`HttpMachine: Requesting: onDone: event.data.version = ${event.output.version}, window.golite = `, window?.golite);
                                if (window?.golite?.version !== (event.output as any).version) {
                                    window.golite = {
                                        ...window.golite,
                                        settings: window.golite.settings,
                                        version: (event.output as any).version,
                                        hasUpdate: window?.golite?.version !== undefined
                                    } as GlobalData;
                                    console.debug(`HttpMachine: Requesting: onDone: window.golite = `, window?.golite);
                                }
                                if (context.triggerSiteMessageChangeMessage) {
                                    if (!!event.output.siteMessageHash) {
                                        const record = JSON.parse(sessionStorage.getItem(`SiteMessage-${event.output.siteMessageHash}`) ?? "{}");
                                        if (!record.notified) {
                                            console.debug(`HttpMachine: Requesting: onDone: dispatching SiteMessageSetEvent...`);
                                            window.dispatchEvent(new SiteMessageSetEvent());
                                            sessionStorage.setItem(`SiteMessage-${event.output.siteMessageHash}`, JSON.stringify({ notified: true }));
                                        }
                                    } else {
                                        console.debug(`HttpMachine: Requesting: onDone: dispatching SiteMessageClearedEvent...`);
                                        window.dispatchEvent(new SiteMessageClearedEvent());
                                    }
                                }
                            },
                        ],
                    },
                    onError: [
                        {
                            guard: ({ context, event }) =>
                                context.unauthorisedAutoRedirect === true && (event.error as any).status === 401,
                            target: States.Unauthorised,
                            actions: assign({
                                responseEnvelope: ({ context, event }) => {
                                    const responseEnvelope = {
                                        data: undefined,
                                        totalCount: undefined,
                                        problemDetails: {
                                            detail: "Unauthorised, redirecting to sign in...",
                                            status: 401,
                                            title: "Unauthorised, redirecting to sign in...",
                                            validationErrors: [],
                                        },
                                    };
                                    //console.debug(`HttpMachine: onError: assign: event = `,event,`, responseEnvelope = `,responseEnvelope);
                                    return responseEnvelope;
                                },
                            }),
                        },
                        {
                            guard: Guards.IsRequestError,
                            target: States.RequestError,
                            actions: Actions.AssignErrorMessage,
                        },
                        // everything else is classed as a connection error and can be retried
                        {
                            guard: ({ context, event }) => {
                                //console.debug(`ConnectionError: '': guard: event = `, event, `, context.callCount = ${context.callCount}, context.retryCount = ${context.retryCount}.`);
                                return context.callCount <= context.retryCount;
                            },
                            target: States.PendingRetry,
                            actions: Actions.AssignErrorMessage,
                        },
                        {
                            target: States.ConnectionError,
                            actions: Actions.AssignErrorMessage,
                        },
                    ],
                },
                on: {
                    [Events.Abort]: States.Aborted,
                },
                entry: assign({ callCount: ({ context, event }) => context.callCount + 1 }),
            },
            [States.Success]: {
                type: "final",
                on: {
                    [Events.Refresh]: {
                        guard: ({ context, event }) => context.method === "get",
                        target: States.Init,
                    },
                },
            },
            [States.ConnectionError]: {
                type: "final",
                entry: enqueueActions(({ context }) => {
                    //console.debug(`ConnectionError: entry: context.isChildMachine = ${context.isChildMachine}.`);
                    if (context.isChildMachine)
                        if (context.responseEnvelope?.problemDetails)
                            throw new PaygError(context.responseEnvelope?.problemDetails);
                        else
                            throw new Error("Connection Error", { cause: context.responseEnvelope });

                }),
            },
            [States.RequestError]: {
                type: "final",
                entry: enqueueActions(({context, event, enqueue}) => {
                    //console.debug(`ConnectionError: entry: context.isChildMachine = ${context.isChildMachine}.`);
                    if (context.isChildMachine)
                        if (context.responseEnvelope?.problemDetails)
                            throw new PaygError(context.responseEnvelope?.problemDetails);
                        else
                            throw new Error("Request Error", { cause: context.responseEnvelope });
                }),
            },
            [States.Unauthorised]: {
                type: "final",
                entry: enqueueActions(({ context, event }) => {
                    console.debug(`Unauthorised: entry: redirecting to sign in page...`);
                    openLoadingScreen();
                    localStorage.setItem("redirectPath", window.location.pathname + window.location.search);
                    redirectToSignIn(true);
            
                    if (context.isChildMachine)
                        if (context.responseEnvelope?.problemDetails) {
                            //console.debug(`Unauthorised: escalating `, context.responseEnvelope, `...`);
                            throw new PaygError(context.responseEnvelope?.problemDetails);
                        }
                        else {
                            //console.debug(`Unauthorised: escalating `, context.responseEnvelope, `...`);
                            throw new Error("Request Error", { cause: context.responseEnvelope });
                        }
                }),
            },
            [States.PendingRetry]: {
                on: {
                    [Events.Abort]: States.Aborted,
                },
                after: {
                    pendingRetryTimeout: {
                        target: States.Requesting,
                    }
                },            },
            [States.Aborted]: {
                type: "final",
                entry: ({ context, event }) => context.abortController.abort(),
            },
        },
        output: ({ context }) => {
            const result = {
                responseEnvelope: context.responseEnvelope,
            };
            //console.debug(`HttpMachine: output: result = `, result);
            return result;
        },
    }
);

export const doFetch = async (
    url: string,
    method: "get" | "post" | "put" | "delete",
    requestData: FormData | object | undefined,
    contentType: string,
    bearerToken?: string,
    abortSignal?: AbortSignal
) => {
    //console.debug(`HttpMachine: doFetch (${url}): requestData = `, requestData, `, typeof requestData = [${requestData}], instanceof requestData = [${(requestData instanceof FormData)}]`);
    let response: Response;
    try {
        let headers = new Headers();
        if (contentType !== "multipart/form-data") {
            // if this content type, browser will set it automatically
            headers.set("Content-Type", contentType);
        }
        if (bearerToken !== undefined) {
            headers.set("Authorization", `Bearer ${bearerToken}`);
        }
        const correlationId = getCorrelationId();
        if (correlationId) {
            headers.set('X-Correlation-Id', correlationId);
        }
        response = await fetch(url, {
            method,
            body: requestData instanceof FormData ? requestData : requestData ? JSON.stringify(requestData) : undefined,
            credentials: "same-origin",
            headers: headers,
            signal: abortSignal,
        });
        let responseEnvelope: ResponseEnvelope | undefined;
        try {
            responseEnvelope = await response.json();
        } catch (error) {
            // Swallow exception because it is probably that there is no body content
        }

        const result = {
            status: response.status,
            responseEnvelope,
            version: response.headers.get("x-golite-version"), 
            siteMessageHash: response.headers.get("x-payg-sitemessagehash")
        };
        //console.debug(`HttpMachine: doFetch: result: `, result);
        if (!response.ok) throw result;
        return result;
    } catch (e: any) {
        if (e.status !== 401) {
            console.error(`HttpMachine: doFetch: exception: `, e);
    
            let errorMessage = e?.data?.problemDetails?.title ?? e?.message;
            try {
                errorMessage ??= JSON.stringify(e);
            } catch (error) {
                // Swallow exception because it's probably a circular reference or something
            }
            errorMessage ??= "An unknown error occurred";

            datadogLogs.logger.error(`HttpMachine: doFetch: exception: ${errorMessage}`, { exception: e, request: { url, method, contentType } } );
        }
        throw e;
    }
};


export class SiteMessageSetEvent extends CustomEvent<{}> {
    static name = "com.gosweetspot.payg.siteMessage.set";

    constructor() {
        super(SiteMessageSetEvent.name, {
            bubbles: true,
            cancelable: true,
        });
    }
}
export class SiteMessageClearedEvent extends CustomEvent<{}> {
    static name = "com.gosweetspot.payg.siteMessage.cleared";

    constructor() {
        super(SiteMessageClearedEvent.name, {
            bubbles: true,
            cancelable: true,
        });
    }
}